/* eslint-disable no-nested-ternary */
// src/hooks/useApproxContactsCount.js
import { useEffect, useMemo, useRef, useState } from 'react';

// util simple pour ne pas appeler si aucun filtre
const isEmptyFilters = (f) =>
  (!f.filterEvent || f.filterEvent.length === 0) &&
  (!f.filterInterest || f.filterInterest.length === 0) &&
  (!f.filterNbEvent?.min && !f.filterNbEvent?.max) &&
  (!f.filterBillet?.min && !f.filterBillet?.max) &&
  (!f.totalDepense?.min && !f.totalDepense?.max) &&
  (!f.lastDateOrder?.after && !f.lastDateOrder?.before) &&
  (f.filterNewsletter == null) &&
  (f.filterNotif == null) &&
  (f.filterAbonne == null) &&
  (!f.filterHist);

export default function useApproxContactsCount(endpoint, orgID, filters, { debounceMs = 400 } = {}) {
  const [count, setCount] = useState(null);      // nombre approx (entier)
  const [loading, setLoading] = useState(false);
  const [confidence, setConfidence] = useState(null); // 0..1 optionnel
  const [error, setError] = useState(null);

  // cache simple par signature JSON
  const cacheRef = useRef(new Map()); // key -> {count, confidence, ts}
  const abortRef = useRef(null);

  const payload = useMemo(() => ({ orgID, filters }), [orgID, filters]);
  const key = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    if (!endpoint || !orgID || !filters || isEmptyFilters(filters)) {
      setCount(null);
      setConfidence(null);
      setLoading(false);
      setError(null);
      return;
    }

    // cache hit (évite appels répétés sur mêmes filtres)
    const cached = cacheRef.current.get(key);
    if (cached) {
      setCount(cached.count);
      setConfidence(cached.confidence ?? null);
    }

    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const t = setTimeout(async () => {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res}`);
        const data = await res.json();
        console.log(data)
        const nextCount = Number.isFinite(data.approx) ? Math.round(data.approx) : Number.isFinite(data.totalUsers) ? Math.round(data.totalUsers) : null;
        setCount(nextCount);
        setConfidence(data.confidence ?? null);
        cacheRef.current.set(key, { count: nextCount, confidence: data.confidence ?? null, ts: Date.now() });
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, debounceMs);

    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [endpoint, key, orgID, filters, debounceMs]);

  return { count, loading, confidence, error };
}
